// src/components/SheGptSearch.tsx
import React, { useEffect, useRef, useState } from 'react';
import { FiMessageCircle, FiSend } from 'react-icons/fi';

// Wrapper components to fix React 19 typing issues with react-icons
const MessageCircleIcon: React.FC<{ className?: string }> = ({ className }) => {
  const Icon = FiMessageCircle as React.ComponentType<{ className?: string }>;
  return <Icon className={className} />;
};

const SendIcon: React.FC<{ className?: string }> = ({ className }) => {
  const Icon = FiSend as React.ComponentType<{ className?: string }>;
  return <Icon className={className} />;
};

const SCRIPT_ID = 'gen-app-builder-script';

const SheGptSearch: React.FC = () => {
  const widgetRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');

  // Carrega o script do Gen App Builder uma única vez quando o componente monta
  useEffect(() => {
    const existingScript = document.getElementById(SCRIPT_ID);

    if (existingScript) {
      return undefined;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://cloud.google.com/ai/gen-app-builder/client?hl=pt_BR';
    script.async = true;
    document.head.appendChild(script);

    // Função de limpeza para remover o script quando o componente desmontar
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // Cria o elemento customizado após o componente montar
  useEffect(() => {
    const container = widgetRef.current;

    if (container) {
      const widget = document.createElement('gen-search-widget');
      widget.setAttribute('configId', 'd43b7c23-1823-47a7-9f56-69cf04c178e7');
      widget.setAttribute('location', 'us');
      widget.setAttribute('triggerId', 'searchWidgetTrigger');
      container.appendChild(widget);

      return () => {
        if (container.contains(widget)) {
          container.removeChild(widget);
        }
      };
    }
    return undefined;
  }, []);

  // Função para forçar a abertura do modal de conversação
  const handleOpenChat = () => {
    const input = inputRef.current;
    if (!input) return;

    // Simula o clique no input para acionar o widget
    input.click();
    input.focus();

    // Tenta disparar eventos para acionar o widget do Google
    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    input.dispatchEvent(clickEvent);

    const focusEvent = new FocusEvent('focus', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    input.dispatchEvent(focusEvent);

    // Se houver texto no input, tenta enviar
    if (inputValue.trim()) {
      input.value = inputValue;
      const inputEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(inputEvent);
    }
  };

  // Handler para o botão de envio
  const handleSendClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleOpenChat();
  };

  // Handler para a tecla Enter
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleOpenChat();
    }
  };

  return (
    <>
      <div className="w-full">
        <div className="relative mx-auto max-w-2xl overflow-hidden rounded-2xl border border-she-palido-300 bg-white shadow-lg">
          <div className="flex items-center gap-4 bg-gradient-to-r from-she-venoso-500 to-she-arterial-500 px-6 py-5 text-white">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 backdrop-blur">
              <MessageCircleIcon className="text-2xl" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-she-palido-200">Assistente virtual</p>
              <h3 className="text-lg font-semibold">Converse com a SHE GPT</h3>
            </div>
          </div>

          <div className="space-y-4 px-6 py-6">
            <div>
              <label className="sr-only" htmlFor="searchWidgetTrigger">
                Envie uma pergunta para a SHE GPT
              </label>
              <div className="relative">
                <input
                  ref={inputRef}
                  id="searchWidgetTrigger"
                  type="text"
                  placeholder="Clique aqui para iniciar uma conversa..."
                  autoComplete="off"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full rounded-xl border border-she-palido-300 bg-gray-50 px-4 py-3 pr-12 text-gray-700 placeholder-she-arterial-300 shadow-inner transition focus:border-she-arterial-400 focus:outline-none focus:ring-2 focus:ring-she-arterial-400"
                />
                <button
                  type="button"
                  onClick={handleSendClick}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-she-arterial-500 hover:text-she-arterial-600 transition-colors cursor-pointer"
                  aria-label="Enviar mensagem"
                >
                  <SendIcon className="text-xl" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* O widget de busca em si (invisível até ser acionado) */}
      <div ref={widgetRef} />
    </>
  );
};

export default SheGptSearch;